import sha1 from 'crypto-js/sha1';
import { derived, get, writable, type Writable } from 'svelte/store';
import { replace } from 'svelte-spa-router';

import { CURRENT_UNIT_ID_KEY } from 'src/constants';
import { isValidJSON } from 'src/helpers/validators';
import { mapHomesResponse } from 'src/helpers/houses/homes_helpers';
import { mergeGetUnitsInfoIntoUserData } from 'src/helpers/houses/merge_get_units_info_into_user_data';
import { notifyNativeHomes } from 'src/helpers/native_bridge_helpers';
import {
	addSharedDevice,
	changeDeviceOwner,
	deleteSharedDevice,
	getUnitsInfo,
	transferDevicesToUnit,
} from 'src/services/iotu/house-management';
import { getLandingHref } from 'src/helpers/main_menu';
import { forceLogout, switchUnit } from 'src/services/auth_v2';
import { invalidateAccountSessions } from 'src/services/unit_token_cache';
import { switchToAnySavedProfile } from 'src/components/Profiles/profiles_helpers';
import { toast } from '@Shelly/shelly-design-system/index';
import { format } from 'svelte-i18n';
import { normalizeEmail } from 'src/helpers/email_helpers';
import type { Device } from 'src/types/Device';
import type { Home } from 'src/types/Home';
import type { SubUnitMap } from 'src/types/SubUnit';
import type { GetUnitsInfoResponse } from 'src/types/responses/getUnitsInfoResponse';

import { devices } from '../lists/devices';
import { user_data, user_settings } from './user';

const createSubUnitsStore = () => {
	const { subscribe, set, update } = writable<SubUnitMap>({});

	return {
		subscribe,
		set,
		update: (data: SubUnitMap) =>
			update((store) => {
				return { ...store, ...data };
			}),
		remove: (id: string) =>
			update((store) => {
				delete store[id];
				return store;
			}),
		reset: () => set({}),
	};
};

export const sub_units = createSubUnitsStore();

/** Remote fetch lifecycle for POST `/user/get_units_info`. */
export type HomesFetchStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * Store for the home picker. Single source of truth for the list
 * of homes (owned + shared) shown in the new `HomesDropdown`.
 *
 * Lifecycle:
 * - `refreshHomes()` refetches from POST `/user/get_units_info`, updates
 *   this store and merges `available_units` / `available_sub_units` into
 *   `user_data` (also used by home management after unit/share changes).
 * - Called after login, after `switchUnit`, after successful add-unit flows,
 *   and defensively on first dropdown open.
 * - `clearHomes()` is called on logout to drop stale state.
 */
export interface HomesState {
	list: Home[];
	fetchStatus: HomesFetchStatus;
	error?: string;
}

const HOMES_STORAGE_KEY = 'homes_list';

/** Load cached homes list from localStorage */
const loadCachedHomes = (): HomesState => {
	const cached = localStorage.getItem(HOMES_STORAGE_KEY);
	if (cached && isValidJSON(cached)) {
		const list = JSON.parse(cached) as Home[];
		if (Array.isArray(list) && list.length > 0) {
			return { list, fetchStatus: 'success' };
		}
	}
	return { list: [], fetchStatus: 'idle' };
};

const initialState: HomesState = loadCachedHomes();

export const homes: Writable<HomesState> = writable<HomesState>({
	...initialState,
});

homes.subscribe((state) => {
	localStorage.setItem(HOMES_STORAGE_KEY, JSON.stringify(state.list));
});

let inflight: Promise<boolean> | null = null;
let inflightEmail: string | null = null;
let revokeRecoveryInflight = false;

type PrimedUnitsInfo = { email: string; response: GetUnitsInfoResponse; ts: number };
let primedUnitsInfo: PrimedUnitsInfo | null = null;
const PRIMED_UNITS_TTL_MS = 60_000;

/**
 * Store a `get_units_info` response the picker already fetched, so the imminent
 * post-commit refresh for the same account can reuse it instead of refetching.
 */
export const primeUnitsInfo = (email: string, response: GetUnitsInfoResponse): void => {
	if (!response?.isok) return;
	primedUnitsInfo = { email: normalizeEmail(email), response, ts: Date.now() };
};

export const clearPrimedUnitsInfo = (): void => {
	primedUnitsInfo = null;
};

/**
 * Consume-once: return a primed response only if it matches this account and is
 * still fresh. Always clears (even on mismatch/expiry) so a stale prime can't
 * linger into unrelated refreshes.
 */
const takePrimedUnitsInfo = (email: string): GetUnitsInfoResponse | null => {
	const primed = primedUnitsInfo;
	primedUnitsInfo = null;
	if (!primed) return null;
	if (normalizeEmail(email) !== primed.email) return null;
	if (Date.now() - primed.ts > PRIMED_UNITS_TTL_MS) return null;
	return primed.response;
};

/**
 * Toast for a lost-home situation, deferred a tick so it survives the
 * switch/logout reload that recovery triggers. Defaults to the "Home access
 * removed" copy; callers pass their own for deleted / transferred homes.
 */
export const showLostHomeToast = (title?: string, text?: string): void => {
	setTimeout(() => {
		const _format = get(format);
		toast.open({
			title: title || _format('home_access_revoked', { default: 'Home access removed' }),
			text:
				text ||
				_format('home_access_revoked_description', {
					default: 'You no longer have access to this home.',
				}),
			type: 'danger',
			dangerWithProgress: true,
		});
	}, 0);
};

/**
 * Recover the session when the active home is gone (revoked share, deletion,
 * or transfer): switch to another owned home → another saved profile → force
 * logout. Single-flight via `revokeRecoveryInflight`.
 *
 * @param notify Toast shown once recovery lands. Defaults to "Home access removed".
 */
export const recoverFromRevokedAccess = async (
	notify: () => void = () => showLostHomeToast(),
): Promise<void> => {
	if (revokeRecoveryInflight) return;
	revokeRecoveryInflight = true;

	try {
		const ownedUnitIds = Object.keys(get(user_data)?.available_units ?? {});
		const activeId = get(currentUnitId);
		const activeEmail = get(user_data)?.email;
		const fallbackOwnedId = ownedUnitIds.find((id) => id !== activeId) ?? ownedUnitIds[0];

		// Access to the active home was revoked. Drop this account's cached unit
		// sessions so the revoked home (and any other now-stale session) is never
		// re-served from cache; the fallback switch below performs a real login.
		if (activeEmail) invalidateAccountSessions(activeEmail);

		if (fallbackOwnedId) {
			try {
				await switchUnit(fallbackOwnedId);
				notify();
				replace(getLandingHref());
				return;
			} catch (error) {
				console.error(
					'[homes] failed to switch to fallback home during revoked-access recovery',
					error,
				);
			}
		}

		if (await switchToAnySavedProfile(activeEmail)) {
			notify();
			return;
		}

		await forceLogout(notify);
	} catch (error) {
		console.error('[homes] revoked-access recovery failed', error);
	} finally {
		revokeRecoveryInflight = false;
	}
};

/**
 * Fetch the latest homes via POST `/user/get_units_info`, merge
 * `available_units` / `available_sub_units` into `user_data`, and write
 * the mapped `Home[]` to this store. Concurrent calls share one
 * in-flight request.
 * `currentUnitId` — it's the only reliable
 * active-home identifier.
 */
export const refreshHomes = async (updateNatives = true, email?: string): Promise<boolean> => {
	const forEmail = normalizeEmail(get(user_data)?.email ?? '');

	// Don't reuse an in-flight request from a different account (FE-2197).
	if (inflight && inflightEmail === forEmail) return inflight;

	homes.update((state) => ({ ...state, fetchStatus: 'loading', error: undefined }));

	inflightEmail = forEmail;
	inflight = (async (): Promise<boolean> => {
		try {
			// Reuse the picker's already-fetched response for the imminent commit
			// refresh (same account, same account-scoped data); otherwise fetch.
			const response = takePrimedUnitsInfo(forEmail) ?? (await getUnitsInfo());

			// Account changed mid-flight (fast profile switch): discard a response
			// that no longer matches the account it was started for (FE-2197).
			if (normalizeEmail(get(user_data)?.email ?? '') !== forEmail) {
				return false;
			}

			if (!response?.isok) {
				const inSharedHomeSession =
					typeof get(user_settings)?.sub === 'number' ||
					get(user_data)?.is_sub === true ||
					get(homes).list.some((home) => home.isActive && home.isShared);

				if (inSharedHomeSession) {
					void recoverFromRevokedAccess();
					return false;
				}
				throw new Error(response?.error || 'failed');
			}

			const list = mapHomesResponse(response, get(currentUnitId));

			mergeGetUnitsInfoIntoUserData(response);
			homes.set({ list, fetchStatus: 'success' });

			if (updateNatives) {
				notifyNativeHomes(list, email);
			}
			return true;
		} catch (error) {
			console.error('[homes] refresh failed', error);
			homes.set({
				list: [],
				fetchStatus: 'error',
				error: error instanceof Error ? error.message : String(error),
			});
			notifyNativeHomes([]);
			return true;
		} finally {
			// Keep tracking if a newer refresh for another account took over.
			if (inflightEmail === forEmail) {
				inflight = null;
				inflightEmail = null;
			}
		}
	})();

	return inflight;
};

/**
 * Drop a home from the picker list (and the cached `available_*` maps) after a
 * switch attempt reveals it is gone — deleted, unshared, or transferred away —
 * so the stale entry disappears from the dropdown immediately without waiting
 * for a full refresh (FE-2022). Shared homes are matched on `unitId` + `subId`;
 * owned homes on `unitId` alone.
 */
export const removeHomeFromList = (unitId?: string, subId?: string): void => {
	if (!unitId) return;

	homes.update((state) => ({
		...state,
		list: state.list.filter((home) =>
			subId != null
				? !(home.id === unitId && String(home.sub) === String(subId))
				: home.id !== unitId,
		),
	}));

	const data = get(user_data);
	const available_units = { ...(data?.available_units ?? {}) };
	const available_sub_units = { ...(data?.available_sub_units ?? {}) };
	if (subId != null) {
		delete available_sub_units[unitId];
	} else {
		delete available_units[unitId];
	}
	user_data.update({ available_units, available_sub_units });
};

export const clearHomes = (): void => {
	homes.set({ list: [], fetchStatus: 'idle' });
	currentUnitId.set('');
	sub_units.reset();
	notifyNativeHomes([]);
};

/** Sub-unit id for a shared home id, or `undefined` when not shared / not in the store. */
export const getSubIdForHome = (id: string): number | undefined =>
	get(homes).list.find((home) => home.id === id && home.isShared)?.sub;

// ---------------------------------------------------------------------------
// Derived device stores for house-management UI components
// ---------------------------------------------------------------------------

/**
 * Main (non-channel) devices owned by the current user.
 * Excludes shared devices and channel sub-devices (ids containing '_').
 */
export const ownedDevices = derived(
	devices,
	($devices) =>
		Object.values($devices).filter((d: Device) => !d.shared && !d.id.includes('_')) as Device[],
);

/**
 * Devices the current user has shared with others.
 * Excludes channel sub-devices (ids containing '_').
 */
export const sharedByMeDevices = derived(
	devices,
	($devices) =>
		Object.values($devices).filter((d: Device) => d.shared_by_me && !d.id.includes('_')) as Device[],
);

/** The current account's active unit id, as a string. */
export const currentUnitId = writable<string>(localStorage.getItem(CURRENT_UNIT_ID_KEY) ?? '');

currentUnitId.subscribe((val) => {
	localStorage.setItem(CURRENT_UNIT_ID_KEY, val ?? '');
});

/**
 * Available units (homes) the user can move devices to.
 * Excludes the current unit so it cannot be selected as a destination.
 * Each entry has `{ title: string; value: string }`.
 */
export const unitOptions = derived([user_data, currentUnitId], ([$ud, $currentUnitId]) =>
	Object.entries($ud.available_units ?? {})
		.filter(([id]) => id !== $currentUnitId)
		.map(([id, name]) => ({ title: name as string, value: id })),
);

// ---------------------------------------------------------------------------
// Async action helpers for house-management operations
// ---------------------------------------------------------------------------

type RejectedShareReason = {
	response?: { data?: { errors?: Record<string, unknown> } };
};

function extractFirstErrorKey(result: PromiseSettledResult<unknown>): string | undefined {
	const errors =
		result.status === 'fulfilled'
			? (result.value as { errors?: Record<string, unknown> })?.errors
			: (result.reason as RejectedShareReason)?.response?.data?.errors;
	if (errors && typeof errors === 'object') {
		const keys = Object.keys(errors);
		return keys.length > 0 ? keys[0] : undefined;
	}
	return undefined;
}

/** Result of a batch operation that processes multiple devices independently. */
export type BatchResult = {
	succeeded: string[];
	failed: string[];
	/** First API `errors` object key from a failed `add_shared` response (for i18n). */
	firstErrorKey?: string;
	/** Per failed device id: first key from API `errors` (when present). */
	failureErrorKeys: Partial<Record<string, string>>;
};

/**
 * Share one or more devices with a recipient.
 * Calls `POST /device/add_shared` for each device in parallel and marks successful ones as
 * `shared_by_me` in the local store.
 */
export async function shareDevicesAction(
	deviceIds: string[],
	recipientEmail: string,
	channel = 0,
): Promise<BatchResult> {
	const results = await Promise.allSettled(
		deviceIds.map((id) => addSharedDevice(id, recipientEmail, channel)),
	);

	const succeeded: string[] = [];
	const failed: string[] = [];
	const failureErrorKeys: Partial<Record<string, string>> = {};
	let firstErrorKey: string | undefined;

	results.forEach((result, i) => {
		const id = deviceIds[i];
		if (result.status === 'fulfilled' && result.value.isok) {
			succeeded.push(id);
			const current = get(devices)[id];
			if (current) {
				devices.update({ ...current, shared_by_me: true });
			}
		} else {
			failed.push(id);
			const errKey = extractFirstErrorKey(result);
			if (errKey !== undefined) {
				failureErrorKeys[id] = errKey;
			}
			if (!firstErrorKey) {
				firstErrorKey = errKey;
			}
		}
	});

	return { succeeded, failed, firstErrorKey, failureErrorKeys };
}

/**
 * Revoke all shared access for a single device.
 * Internally fetches the recipient list via `GET /device/get_shared` and then deletes each one.
 * On success, removes the `shared_by_me` flag from the local store.
 */
export async function revokeShareAction(id: string, channel = 0): Promise<{ isok: boolean }> {
	const result = await deleteSharedDevice(id, undefined, channel);
	if (!result.isok) {
		return { isok: false };
	}

	const current = get(devices)[id];
	if (current) {
		const { shared_by_me: _, ...rest } = current;
		devices.update(rest as any);
	}

	return { isok: true };
}

function extractTransferErrorMessage(result: unknown, reason?: unknown): string | undefined {
	if (result && typeof result === 'object') {
		const msg = (result as { errors?: { message?: unknown } }).errors?.message;
		if (typeof msg === 'string') return msg;
	}
	if (!reason || typeof reason !== 'object') return undefined;
	const fromReason = (reason as { response?: { data?: { errors?: { message?: unknown } } } }).response
		?.data?.errors?.message;
	return typeof fromReason === 'string' ? fromReason : undefined;
}

/** Result of transfer ownership (`POST /device/change_owner`). */
export type TransferOwnershipResult = {
	isok: boolean;
	/** API `errors.message` when `isok` is false (fulfilled or HTTP error body). */
	errorMessage?: string;
};

/**
 * Transfer ownership of devices to another account.
 * The recipient's password is SHA1-hashed before sending (matches the v2 login flow).
 * On success, removes the devices from the local store.
 */
export async function transferOwnershipAction(
	deviceIds: string[],
	email: string,
	password: string,
): Promise<TransferOwnershipResult> {
	const passwordHash = sha1(password).toString();
	try {
		const result = await changeDeviceOwner(deviceIds, email, passwordHash);
		if (!result.isok) {
			return { isok: false, errorMessage: extractTransferErrorMessage(result) };
		}

		deviceIds.forEach((id) => devices.remove(id));
		return { isok: true };
	} catch (reason: unknown) {
		return { isok: false, errorMessage: extractTransferErrorMessage(null, reason) };
	}
}

/**
 * Move devices from the current unit (home) to another unit belonging to the same account.
 * On success, removes the devices from the local store (they now live in the target unit).
 */
export async function moveDevicesToUnitAction(
	deviceIds: string[],
	targetUnitId: number,
): Promise<{ isok: boolean }> {
	const result = await transferDevicesToUnit(deviceIds, targetUnitId);
	if (!result.isok) {
		return { isok: false };
	}

	deviceIds.forEach((id) => devices.remove(id));
	return { isok: true };
}
